import {flatMap, groupBy, isEmpty, isObject, orderBy, partition, pick, sortBy, unionBy, uniq} from 'lodash';

import {CatalogConfigType} from '../../../../provider/catalogs/catalogs-type';
import {WeekDaysEnum} from '../../../../provider/locale/locale-context-const';
import {ShortLocaleNameEnum} from '../../../../provider/locale/locale-context-type';
import {CompanyKeyEnum, ProtocolEnum} from '../../../../service/company-v2/company-const';
import {CompanyFormType, CompanyServerType, CompanyType} from '../../../../service/company-v2/company-type';
import {CompanyPhoneType} from '../../../../service/company-v2/types/company-contacts-type';
import {CompanyImageFormType} from '../../../../service/company-v2/types/company-logo-type';
import {WorkingDaysType, WorkingDayType} from '../../../../service/company-v2/types/company-working-time-type';
import {ProvidersIdsEnum} from '../../../../util/type';
import {CompanyWidgetKeyEnum} from '../company-form-const';
import {CompanyFieldFillType} from '../company-form-type';

import {isCatalogAvailableInCountry} from './catalog-list/catalog-list-helper';
import {initialCompanyValues} from './create/create-company-form-const';
import {
    convertAttributesToFormFormat,
    convertAttributesToServerFormat,
} from './widgets/attributes/attributes-form-helper';
import {AttributeType, AttributeTypeEnum, BooleanAttributeType} from './widgets/attributes/attributes-type';
import {catalogCategoryLimits} from './widgets/categories/categories-widget-const';
import {
    OPENING_DATE_FORMAT,
    toOpeningDateDayjs,
} from './widgets/working-time/opening-soon-form-item/opening-soon-form-item-helper';

export function getWorkingDaysObject(workingDaysEntries: Array<[string, WorkingDayType | null]>): WorkingDaysType {
    return Object.fromEntries(workingDaysEntries) as WorkingDaysType;
}

function camelizeWorkingHours(workingHours: WorkingDaysType): WorkingDaysType {
    return getWorkingDaysObject(
        Object.entries(workingHours)
            .filter(([, dayWorkingHours]) => dayWorkingHours)
            .map(([dayName, dayWorkingHours]) => [dayName.replace('day', 'day_'), dayWorkingHours])
    );
}

function getCityId(city: CompanyFormType['address']['city']) {
    if (typeof city === 'string') {
        return null;
    }

    return city?.id;
}

function getCityName(city: CompanyFormType['address']['city']) {
    if (typeof city === 'string') {
        return city.length > 0 ? city : null;
    }

    return city?.name;
}

function serializeExtraPhoneEntry(catalogId: number, phoneEntry: CompanyPhoneType): CompanyPhoneType {
    const {phone, isMain, description} = phoneEntry;
    const basePhone: CompanyPhoneType = {phone, isMain};

    if (catalogId === ProvidersIdsEnum.yandex) {
        return {...basePhone, description: description?.trim() || ''};
    }

    if (catalogId === ProvidersIdsEnum.doubleGis && description?.trim()) {
        return {...basePhone, description: description.trim()};
    }

    return basePhone;
}

function getActiveCatalogIds(initialValues: Partial<CompanyType>, catalogList: Array<CatalogConfigType>) {
    const countryId = initialValues.address?.countryId;

    return new Set(
        catalogList
            .filter(({canRewriteCategories}) => canRewriteCategories)
            .filter((catalog) => (countryId ? isCatalogAvailableInCountry(catalog, countryId) : true))
            .map(({catalogId}) => catalogId)
    );
}

function assignMainIfDoesntExist<T extends {isMain?: boolean}>(collection?: Array<T>) {
    if (!collection) {
        return [];
    }

    return orderBy(collection, ({isMain}) => isMain, ['desc']).map((item, index) =>
        index === 0 && !item.isMain ? {...item, isMain: true} : item
    );
}

export type MessengerRegexpEntryType = {
    regexp: string;
    protocol: ProtocolEnum;
};

type ExtractProtocolOptionsType = {
    messengerRegexps?: Array<MessengerRegexpEntryType>;
};

const MAX_HOST_REGEXP = /^https?:\/\/max\.ru\//i;
const MAX_HOST_PATH_REGEXP = /^max\.ru\//i;

function stripMaxLinkScheme(value: string): string {
    return value.trim().replace(/^https?:\/\//i, '');
}

/** Strips scheme and duplicate max.ru host — used only when building the payload for the API. */
function normalizeMaxSocialNetworkValue(value: string): string {
    return stripMaxLinkScheme(value).replace(/^max\.ru\//i, '');
}

function isMaxSocialNetworkLink(link: string, messengerRegexps?: Array<MessengerRegexpEntryType>): boolean {
    const trimmed = link.trim();

    if (trimmed.startsWith(ProtocolEnum.MAX) || MAX_HOST_REGEXP.test(trimmed) || MAX_HOST_PATH_REGEXP.test(trimmed)) {
        return true;
    }

    const maxRegexp = messengerRegexps?.find(({protocol}) => protocol === ProtocolEnum.MAX);

    return Boolean(maxRegexp && new RegExp(maxRegexp.regexp).test(trimmed));
}

function tryExtractMaxProtocol(
    link: string,
    messengerRegexps?: Array<MessengerRegexpEntryType>
): {value: string; protocol: ProtocolEnum} | null {
    if (!isMaxSocialNetworkLink(link, messengerRegexps)) {
        return null;
    }

    if (link.startsWith(ProtocolEnum.MAX)) {
        return {value: link.replace(ProtocolEnum.MAX, ''), protocol: ProtocolEnum.MAX};
    }

    return {value: link.trim(), protocol: ProtocolEnum.MAX};
}

function formatSocialNetworkUrlForServer(protocol: ProtocolEnum, value: string): string {
    if (protocol === ProtocolEnum.MAX) {
        return ProtocolEnum.MAX + normalizeMaxSocialNetworkValue(value);
    }

    return protocol + value;
}

export function extractProtocol(
    link: string,
    options?: ExtractProtocolOptionsType
): {value: string; protocol: ProtocolEnum} | null {
    const trimmed = link.trim();

    if (options?.messengerRegexps?.length) {
        const matched = options.messengerRegexps.find(({regexp}) => new RegExp(regexp).test(trimmed));

        if (matched) {
            if (matched.protocol === ProtocolEnum.MAX) {
                return tryExtractMaxProtocol(trimmed, options.messengerRegexps);
            }

            return {
                value: trimmed.replace(/^https?:\/\//, ''),
                protocol: matched.protocol,
            };
        }
    }

    // MAX is a social-network-only protocol: only auto-detect a max.ru link as MAX when the
    // caller enables it (a MAX entry in messengerRegexps). Otherwise a max.ru URL pasted into a
    // plain website field would be mislabelled as MAX, showing a bogus prefix and duplicating it
    // on save (`protocol + value` => "MAXhttps://max.ru/...").
    const isMaxAllowed = Boolean(options?.messengerRegexps?.some(({protocol}) => protocol === ProtocolEnum.MAX));
    const maxProtocol = isMaxAllowed ? tryExtractMaxProtocol(trimmed, options?.messengerRegexps) : null;

    if (maxProtocol) {
        return maxProtocol;
    }

    if (link.startsWith(ProtocolEnum.WHATSAPP)) {
        return {value: link.replace(ProtocolEnum.WHATSAPP, ''), protocol: ProtocolEnum.WHATSAPP};
    }

    if (link.startsWith(ProtocolEnum.VIBER)) {
        return {value: link.replace(ProtocolEnum.VIBER, ''), protocol: ProtocolEnum.VIBER};
    }

    if (link.startsWith(ProtocolEnum.HTTP)) {
        return {value: link.replace(ProtocolEnum.HTTP, ''), protocol: ProtocolEnum.HTTP};
    }

    if (link.startsWith(ProtocolEnum.HTTPS)) {
        return {value: link.replace(ProtocolEnum.HTTPS, ''), protocol: ProtocolEnum.HTTPS};
    }

    return null;
}

function extractProtocolWithFallback(link: string) {
    return extractProtocol(link) || {value: link.replace(ProtocolEnum.HTTPS, ''), protocol: ProtocolEnum.HTTPS};
}

/** Maps API social_networks string to form fields; for MAX only strips http(s)://, not max.ru. */
function parseSocialNetworkLinkFromServer(link: string): {protocol: ProtocolEnum; value: string} {
    if (isMaxSocialNetworkLink(link)) {
        return {protocol: ProtocolEnum.MAX, value: stripMaxLinkScheme(link)};
    }

    return extractProtocolWithFallback(link);
}

/** Re-run the same parsing as toFormFormat so dirty-check compares canonical protocol + value. */
export function normalizeSocialNetworkFormItemForDirty(item: {protocol: ProtocolEnum; value: string}): {
    protocol: ProtocolEnum;
    value: string;
} {
    if (item.protocol === ProtocolEnum.MAX) {
        return item;
    }

    return extractProtocolWithFallback(`${item.protocol}${item.value ?? ''}`);
}

const WHATSAPP_URL_REGEXP =
    /^(?:https?:\/\/)?(?:wa\.me|api\.whatsapp\.com)\/(?:\d+|(?:send(?:\?|\/\?)phone=\d+))(?:(?:\?|\/\?|&)text=?.*)?$/;
const VIBER_URL_REGEXP = /^(?:https?:\/\/)?viber\.click\/(\w{2,32})\/?$|(?:viber:\/\/chat\?number=\+?([\d_]{2,32}))/;

function wrapMessengerPhoneToUrl(value: string, messenger: 'whatsapp' | 'viber'): string {
    if (messenger === 'whatsapp') {
        return WHATSAPP_URL_REGEXP.test(value) ? value : `https://wa.me/${value}`;
    }

    return VIBER_URL_REGEXP.test(value) ? value : `viber://chat?number=+${value}`;
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function toFormFormat(
    initialValues: Partial<CompanyType>,
    catalogList: Array<CatalogConfigType>
): Partial<CompanyFormType> {
    const catalogIds = catalogList.map(({catalogId}) => catalogId);
    const activeCatalogIds = getActiveCatalogIds(initialValues, catalogList);
    const catalogsForLinks = uniq([
        ...(initialValues.extraWebsites?.map(({catalogId}) => catalogId) ?? []),
        ...(initialValues.extraSocialNetworks?.map(({catalogId}) => catalogId) ?? []),
    ]);

    const initialCatalogCategoriesInner =
        initialValues.mappedCatalogCategories && initialValues?.mappedCatalogCategories?.length > 0
            ? initialValues.mappedCatalogCategories
            : initialValues.catalogCategories && initialValues?.catalogCategories?.length > 0
            ? initialValues.catalogCategories
            : [];

    const initialCatalogCategories = catalogList
        ?.filter(({catalogId}) => activeCatalogIds.has(catalogId))
        .map((catalog) => {
            const {catalogId} = catalog;
            const limit = catalogCategoryLimits(Boolean(initialValues.isPaidYandexPlacement))[catalogId];
            const changedCatalog = initialValues.catalogCategories?.find(
                (categoryCatalog) => categoryCatalog.catalogId === catalogId
            );

            const catalogCategories = initialCatalogCategoriesInner.find(
                (category) => category.catalogId === catalogId
            );

            const newCatalog = {
                catalogId,
                categories: assignMainIfDoesntExist((changedCatalog || catalogCategories || {}).categories),
                edited: Boolean(changedCatalog),
            };

            if (!limit) {
                return newCatalog;
            }

            return {
                ...newCatalog,
                categories: newCatalog.categories.slice(0, limit),
            };
        });

    const {companyVideos, ...restInitialValues} = initialValues;
    const formValues: Partial<CompanyFormType> = {
        ...restInitialValues,
        email: initialValues.email ?? '',
        legal: {
            name: initialValues.legal?.name || null,
            form: {
                value: initialValues.legal?.formId ?? null,
                label: initialValues.legal?.formName ?? null,
            },
        },
        address: {
            lang: initialValues.address?.lang ?? null,
            country:
                initialValues.address?.countryId && initialValues.address?.countryName
                    ? {
                          id: initialValues.address?.countryId,
                          lang: initialValues.address?.lang ?? ShortLocaleNameEnum.ru,
                          name: initialValues.address?.countryName,
                          countryCode: initialValues.address?.countryCode ?? '',
                      }
                    : null,
            region:
                initialValues.address?.regionId && initialValues.address?.regionName
                    ? {
                          id: initialValues.address?.regionId,
                          name: initialValues.address?.regionName,
                      }
                    : null,
            city:
                initialValues.address?.cityId && initialValues.address?.cityName
                    ? {
                          id: initialValues.address?.cityId,
                          name: initialValues.address?.cityName,
                      }
                    : null,
            street: initialValues.address?.street ?? null,
            houseNumber: initialValues.address?.houseNumber ?? null,
            postalCode: initialValues.address?.postalCode ?? null,
            description: initialValues.address?.description ?? null,
            latLng:
                initialValues.address?.lat && initialValues.address?.lon
                    ? ([initialValues.address?.lat, initialValues.address?.lon] as [number, number])
                    : null,
        },
        rocketdataCategories: assignMainIfDoesntExist(initialValues.rocketdataCategories),
        mappedCatalogCategories: initialValues.mappedCatalogCategories?.map((catalogCategories) => ({
            ...catalogCategories,
            categories: assignMainIfDoesntExist(catalogCategories.categories),
        })),
        catalogCategories:
            initialValues.mappedCatalogCategories?.length || initialValues.catalogCategories?.length
                ? initialCatalogCategories
                : [...activeCatalogIds].map((id) => ({catalogId: id, edited: false, categories: []})),
        phones: initialValues.phones?.length
            ? assignMainIfDoesntExist(
                  initialValues.phones.map(({phone, isMain, description}) => ({
                      phone,
                      isMain,
                      description,
                  }))
              )
            : initialCompanyValues.phones,
        websites: initialValues.websites?.length
            ? assignMainIfDoesntExist(
                  initialValues.websites?.map(({website, isMain}) => ({
                      ...extractProtocolWithFallback(website),
                      isMain,
                  }))
              )
            : initialCompanyValues.websites,
        extraWebsites: initialValues.extraWebsites?.map(({catalogId, websites}) => ({
            catalogId,
            websites: websites?.map(({website, isMain, isUseInInfoTag}) => ({
                ...extractProtocolWithFallback(website),
                isMain,
                isUseInInfoTag,
            })),
        })),
        extraPhones: initialValues.extraPhones?.map((extraPhones) => ({
            ...extraPhones,
            phones: sortBy(extraPhones.phones, (phone) => !phone.isMain),
        })),
        socialNetworks: initialValues.socialNetworks?.length
            ? initialValues.socialNetworks?.map((socialNetwork) => parseSocialNetworkLinkFromServer(socialNetwork))
            : initialCompanyValues.socialNetworks,
        extraSocialNetworks: catalogsForLinks.map((catalogId) => {
            const extraField = initialValues.extraSocialNetworks?.find((item) => item.catalogId === catalogId);
            const source = extraField
                ? extraField.socialNetworks?.map((item) => item.value)
                : initialValues.socialNetworks;

            const socialNetworkItems = source?.length
                ? source.map((socialNetwork) => parseSocialNetworkLinkFromServer(socialNetwork))
                : [];

            const messengerItems = flatMap(
                Object.entries(extraField?.messengerPhones || {}),
                ([messenger, phones]) =>
                    phones?.map((phone) => ({
                        value: phone.value,
                        protocol: messenger === 'whatsapp' ? ProtocolEnum.WHATSAPP : ProtocolEnum.VIBER,
                    })) || []
            );

            const allItems = [...socialNetworkItems, ...messengerItems];

            return {
                catalogId,
                socialNetworks: allItems.length > 0 ? allItems : undefined, // eslint-disable-line no-undefined
            };
        }),
        gallery: [
            ...(initialValues.gallery?.map(({hash, url, size}) => ({
                hash,
                url,
                size,
            })) || []),
            ...(companyVideos?.map((videoItem) => ({
                video: videoItem.url,
                thumbnail: videoItem.thumbnailUrl,
                id: videoItem.id,
                catalogId: videoItem.catalogId,
            })) || []),
        ],
        extraGallery: initialValues.extraGallery?.map(({catalogId, gallery}) => ({
            catalogId,
            gallery: gallery.map(({hash, url, size}) => ({
                hash,
                url,
                size,
            })),
        })),
        attributes: convertAttributesToFormFormat(initialValues.attributes),
    };

    // these modifications are necessary for tabs that show several lists inside them.
    // they basically add empty initial values for cases when first and second list have different
    // lengths/values, thus making them have the same structure
    formValues.extraPhones = sortBy(
        unionBy(formValues.extraPhones, initialValues.extraEmails, ({catalogId}) => catalogId),
        ({catalogId}) => catalogIds.indexOf(catalogId)
    ).map(
        ({catalogId}) =>
            formValues.extraPhones?.find((item) => item.catalogId === catalogId) ?? {
                catalogId,
                phones: undefined, // eslint-disable-line no-undefined
            }
    );

    formValues.extraEmails = sortBy(
        unionBy(initialValues.extraPhones, formValues.extraEmails, ({catalogId}) => catalogId),
        ({catalogId}) => catalogIds.indexOf(catalogId)
    ).map(
        ({catalogId}) =>
            formValues.extraEmails?.find((item) => item.catalogId === catalogId) ?? {
                catalogId,
                email: null,
            }
    );

    formValues.extraWebsites = sortBy(
        unionBy(formValues.extraWebsites, initialValues.extraSocialNetworks, ({catalogId}) => catalogId),
        ({catalogId}) => catalogIds.indexOf(catalogId)
    ).map(
        ({catalogId}) =>
            formValues.extraWebsites?.find((item) => item.catalogId === catalogId) ?? {
                catalogId,
                websites: undefined, // eslint-disable-line no-undefined
            }
    );

    formValues.extraSocialNetworks = sortBy(
        unionBy<{catalogId: number}>(
            initialValues.extraWebsites,
            formValues.extraSocialNetworks,
            ({catalogId}) => catalogId
        ),
        ({catalogId}) => catalogIds.indexOf(catalogId)
    ).map(
        ({catalogId}) =>
            formValues.extraSocialNetworks?.find((item) => item.catalogId === catalogId) ?? {
                catalogId,
                socialNetworks: undefined, // eslint-disable-line no-undefined
            }
    );

    formValues.openingDate = initialValues.temporaryClosed
        ? toOpeningDateDayjs(initialValues.openingDate)?.format(OPENING_DATE_FORMAT) ?? null
        : null;

    return formValues;
}

function getRemovedCatalogs(
    serverFormatArray: Array<{catalogId: number}>,
    initialValuesArray?: Array<{catalogId: number}>
) {
    const formCatalogs = new Set(serverFormatArray.map(({catalogId}) => catalogId));
    const initialCatalogs = initialValuesArray?.map(({catalogId}) => catalogId) ?? [];

    return initialCatalogs.filter((catalog) => !formCatalogs.has(catalog));
}

function addEmptyArraysForRemovedCatalogs(serverFormat: CompanyServerType, initialValues: Partial<CompanyFormType>) {
    serverFormat.extraNames.push(
        ...getRemovedCatalogs(serverFormat.extraNames, initialValues?.extraNames).map((catalogId) => ({
            catalogId,
            names: [],
        }))
    );

    serverFormat.extraPhones.push(
        ...getRemovedCatalogs(serverFormat.extraPhones, initialValues?.extraPhones).map((catalogId) => ({
            catalogId,
            phones: [],
        }))
    );

    serverFormat.extraEmails.push(
        ...getRemovedCatalogs(serverFormat.extraEmails, initialValues?.extraEmails).map((catalogId) => ({
            catalogId,
            email: null,
        }))
    );

    serverFormat.extraWebsites.push(
        ...getRemovedCatalogs(serverFormat.extraWebsites, initialValues?.extraWebsites).map((catalogId) => ({
            catalogId,
            websites: [],
        }))
    );

    serverFormat.extraSocialNetworks.push(
        ...getRemovedCatalogs(serverFormat.extraSocialNetworks, initialValues?.extraSocialNetworks).map(
            (catalogId) => ({
                catalogId,
                // backend reads null (not []) as "no extra field", matching the regular per-item mapping above
                socialNetworks: null,
                messengerPhones: null,
            })
        )
    );

    serverFormat.extraWorkingHours.push(
        ...getRemovedCatalogs(serverFormat.extraWorkingHours, initialValues?.extraWorkingHours).map((catalogId) => ({
            catalogId,
            workingHours: getWorkingDaysObject(Object.values(WeekDaysEnum).map((day) => [`day${day}`, null])),
        }))
    );

    serverFormat.extraDescriptions.push(
        ...getRemovedCatalogs(serverFormat.extraDescriptions).map((catalogId) => ({
            catalogId,
            long: null,
        }))
    );

    serverFormat.extraGallery.push(
        ...getRemovedCatalogs(serverFormat.extraGallery, initialValues?.extraGallery).map((catalogId) => ({
            catalogId,
            gallery: [],
        }))
    );
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function toServerFormat(formData: CompanyFormType, initialValues: Partial<CompanyFormType>): CompanyServerType {
    const [resetCategories, activeCategories] = partition(formData.catalogCategories, (category) => category.reset);

    const resetCatalogCategories = resetCategories.map(({catalogId}) => ({catalogId, categories: []}));

    const changedCatalogCategories = activeCategories
        .filter(({edited}) => edited)
        .map(({catalogId, categories}) => ({
            catalogId,
            categories: categories.map(({originId, isMain}) => ({originId, isMain})),
        }));

    const serverFormat: CompanyServerType = {
        ...initialCompanyValues,
        ...formData,
        names: formData.names.map(({name, lang}) => ({name, lang})),
        extraNames:
            formData.extraNames?.map(({catalogId, names}) => ({
                catalogId,
                names: names.map(({name, lang, shortName}) => ({
                    name,
                    lang,
                    ...(catalogId === ProvidersIdsEnum.yandex ? {shortName: shortName ?? ''} : {}),
                })),
            })) ?? initialCompanyValues.extraNames,
        address: {
            lang: formData.address.country?.lang ?? null,
            countryId: formData.address.country?.id ?? null,
            regionId: formData.address.region?.id ?? null,
            regionName: formData.address.region?.name ?? null,
            cityId: getCityId(formData.address.city) ?? null,
            cityName: getCityName(formData.address.city) ?? null,
            street: formData.address.street ?? '',
            houseNumber: formData.address.houseNumber || null,
            postalCode: formData.address.postalCode,
            description: formData.address.description ?? '',
            lat: formData.address.latLng?.[0] ?? 0,
            lon: formData.address.latLng?.[1] ?? 0,
        },
        legal: {
            name: formData.legal.name || null,
            formId: formData.legal.form.value || null,
        },
        catalogCategories: [...resetCatalogCategories, ...changedCatalogCategories],
        extraPhones:
            formData.extraPhones
                ?.filter(
                    ({phones}) => phones?.length === 0 || phones?.some(({phone}) => phone.length) // in one catalog tab we can change email, but leave phone as an empty string
                )
                .map(({catalogId, phones}) => ({
                    catalogId,
                    phones:
                        phones
                            ?.filter(({phone}) => phone && phone.trim().length > 0)
                            .map((phoneEntry) => serializeExtraPhoneEntry(catalogId, phoneEntry)) ?? [],
                })) ?? initialCompanyValues.extraPhones,
        extraEmails:
            formData.extraEmails
                ?.filter((emails) => emails.email !== undefined) // eslint-disable-line no-undefined
                .map((emails) => ({...emails, email: emails.email || null})) ?? initialCompanyValues.extraEmails,
        phones:
            formData.phones
                ?.filter(({phone}) => phone && phone.trim().length > 0)
                .map(({phone, isMain, description}) => ({phone, isMain, description: description?.trim() || ''})) ?? [],
        websites: formData.websites
            .filter(({value}) => value)
            .map(({value, protocol, isMain}) => ({website: protocol + value, isMain})),
        extraWebsites:
            formData.extraWebsites?.map(({catalogId, websites}) => ({
                catalogId,
                websites: websites
                    ?.filter(({value}) => value)
                    .map(({value, protocol, isMain, isUseInInfoTag}) => ({
                        website: protocol + value,
                        isMain,
                        isUseInInfoTag,
                    })),
            })) ?? [],
        socialNetworks: formData.socialNetworks
            .filter(({value}) => value)
            .map(({value, protocol}) => formatSocialNetworkUrlForServer(protocol, value)),
        extraSocialNetworks:
            formData.extraSocialNetworks?.map(({catalogId, socialNetworks}) => {
                if (!socialNetworks?.length) {
                    return {
                        catalogId,
                        socialNetworks: null,
                        messengerPhones: null,
                    };
                }

                const withFormIndex = socialNetworks
                    .map((item, index) => ({...item, formIndex: index}))
                    .filter(({value}) => value);

                const grouped = groupBy(withFormIndex, ({protocol}) => {
                    if (protocol === ProtocolEnum.WHATSAPP) {
                        return 'whatsapp';
                    }

                    if (protocol === ProtocolEnum.VIBER) {
                        return 'viber';
                    }

                    return 'regular';
                });

                const regularLinks =
                    grouped.regular?.map(({value, protocol, formIndex}) => ({
                        value: formatSocialNetworkUrlForServer(protocol, value),
                        index: formIndex,
                    })) || [];
                const messengerPhones = {
                    ...(grouped.whatsapp && {
                        whatsapp: grouped.whatsapp.map(({value, formIndex}) => ({
                            value: wrapMessengerPhoneToUrl(value, 'whatsapp'),
                            index: formIndex,
                        })),
                    }),
                    ...(grouped.viber && {
                        viber: grouped.viber.map(({value, formIndex}) => ({
                            value: wrapMessengerPhoneToUrl(value, 'viber'),
                            index: formIndex,
                        })),
                    }),
                };

                return {
                    catalogId,
                    socialNetworks: regularLinks.length > 0 ? regularLinks : null,
                    messengerPhones: Object.keys(messengerPhones).length > 0 ? messengerPhones : null,
                };
            }) ?? [],
        descriptions: {
            long: formData.descriptions.long || null,
        },
        extraDescriptions:
            formData.extraDescriptions?.map(({catalogId, long}) => ({
                catalogId,
                long: long || null,
            })) ?? initialCompanyValues.extraDescriptions,
        logo: formData.logo
            ? pick<CompanyImageFormType, keyof CompanyImageFormType>(formData.logo, ['tempId', 'hash'])
            : null,
        cover: formData.cover
            ? pick<CompanyImageFormType, keyof CompanyImageFormType>(formData.cover, ['tempId', 'hash'])
            : null,
        gallery: formData.gallery
            ?.filter((item) => {
                const temporaryId = item.tempId;
                const hash = 'hash' in item ? item.hash : null;
                const thumbnail = 'thumbnail' in item ? item.thumbnail : null;

                return (temporaryId || hash) && !thumbnail;
            })
            .map((item) => ({
                tempId: item.tempId,
                hash: 'hash' in item ? item.hash : undefined, // eslint-disable-line no-undefined
            })),
        companyVideos: [
            ...(formData.gallery?.filter((item) => 'video' in item && 'id' in item && item.id) || []),
            ...(formData.gallery
                ?.filter((item) => item.tempId && 'thumbnail' in item && item.thumbnail)
                .map((item) => ({tempId: item.tempId, catalogId: ProvidersIdsEnum.yandex})) ?? []),
        ],
        extraGallery:
            formData.extraGallery?.map(({catalogId, gallery}) => ({
                catalogId,
                gallery:
                    gallery
                        ?.filter((item) => {
                            const temporaryId = item.tempId;
                            const hash = 'hash' in item ? item.hash : null;
                            const thumbnail = 'thumbnail' in item ? item.thumbnail : null;

                            return (temporaryId || hash) && !thumbnail;
                        })
                        .map((item) => ({
                            tempId: item.tempId,
                            hash: 'hash' in item ? item.hash : undefined, // eslint-disable-line no-undefined
                        })) ?? [],
            })) ?? [],
        attributes: convertAttributesToServerFormat(formData.attributes, initialValues.attributes),
    };

    addEmptyArraysForRemovedCatalogs(serverFormat, initialValues);

    if (formData.workingHours) {
        serverFormat.workingHours = camelizeWorkingHours(formData.workingHours);
    }

    if (formData.extraWorkingHours) {
        serverFormat.extraWorkingHours = formData.extraWorkingHours.map(({catalogId, workingHours}) => ({
            catalogId,
            workingHours: workingHours ? camelizeWorkingHours(workingHours) : workingHours,
        }));
    }

    serverFormat.openingDate = formData.temporaryClosed ? formData.openingDate : null;

    return serverFormat;
}

function checkWorkingHours(form: CompanyFormType): boolean {
    if (form.temporaryClosed) {
        return true;
    }

    const fillDays = Object.values(form.workingHours).filter((item) => item !== null);

    return (
        fillDays.length > 0 && (fillDays.every((day) => !isEmpty(day?.timeRange)) || fillDays.some((day) => day?.daily))
    );
}

export function createNewFormFields(form: CompanyFormType, values: Partial<CompanyFormType>): CompanyFormType {
    const companyKey = Object.keys(values)[0] as keyof CompanyFormType;

    switch (companyKey) {
        case CompanyKeyEnum.Descriptions:
            return {...form, descriptions: {...form.descriptions, ...values[companyKey]}};
        case CompanyKeyEnum.Address:
            return {...form, address: {...form.address, ...values[companyKey]}};
        case CompanyKeyEnum.Legal:
            return {...form, legal: {...form.legal, ...values[companyKey]}};
        default:
            return {...form, ...values};
    }
}

/**
 * Mirrors the backend `Address.latlon` check: zero coordinates mean "not set", because the form
 * saves an absent point as lat=0/lon=0.
 */
export function hasCoordinates(latLng: CompanyFormType['address']['latLng']): boolean {
    return Boolean(latLng?.[0] && latLng?.[1]);
}

export function getCompanyCondition(form: CompanyFormType, isDisableLegal: boolean): CompanyFieldFillType {
    return {
        [CompanyWidgetKeyEnum.SearchNames]: form.searchNames.length > 0,
        [CompanyWidgetKeyEnum.Code]: Boolean(form.code),
        [CompanyWidgetKeyEnum.BrandName]: Boolean(form.brandName),
        [CompanyWidgetKeyEnum.PostalCode]: Boolean(form.address.postalCode),
        [CompanyWidgetKeyEnum.City]: isObject(form.address.city)
            ? Boolean(form.address.city.name)
            : Boolean(form.address.city),
        [CompanyWidgetKeyEnum.Street]: Boolean(form.address.street),
        [CompanyWidgetKeyEnum.HouseNumber]: Boolean(form.address.houseNumber) || hasCoordinates(form.address.latLng),
        [CompanyWidgetKeyEnum.LegalName]: isDisableLegal || Boolean(form.legal.name),
        [CompanyWidgetKeyEnum.LegalForm]: isDisableLegal || Boolean(form.legal.form.value),
        [CompanyWidgetKeyEnum.Email]: Boolean(form.email),
        [CompanyWidgetKeyEnum.SocialNetworks]:
            Boolean(form.socialNetworks) && form.socialNetworks.length > 0 && Boolean(form.socialNetworks[0]?.value),
        [CompanyWidgetKeyEnum.WorkingHours]: checkWorkingHours(form),
        [CompanyWidgetKeyEnum.DescriptionsLong]: Boolean(form.descriptions.long),
        [CompanyWidgetKeyEnum.Logo]: form.logo !== null && !isEmpty(form.logo),
        [CompanyWidgetKeyEnum.Cover]: form.cover !== null && !isEmpty(form.cover),
        [CompanyWidgetKeyEnum.Gallery]: form.gallery.length >= 3,
        [CompanyWidgetKeyEnum.Names]: form.names?.every((item) => item.name),
        [CompanyWidgetKeyEnum.Country]: Boolean(form.address.country?.name),
        [CompanyWidgetKeyEnum.Region]: Boolean(form.address.region?.name),
        [CompanyWidgetKeyEnum.Phones]: form.phones?.every((item) => item.phone),
        [CompanyWidgetKeyEnum.RocketdataCategories]:
            form.rocketdataCategories.length > 0 && form.rocketdataCategories?.every((item) => item.categoryName),
        [CompanyWidgetKeyEnum.Website]: form.websites?.every((item) => item.value),
        categories: form.rocketdataCategories.length > 1,
    };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function syncAttributesWithForm(
    attributesState: Map<string, AttributeType>,
    formAttributes: CompanyFormType['attributes']
): CompanyFormType['attributes'] {
    if (!formAttributes || attributesState.size === 0) {
        return formAttributes;
    }

    const updatedAttributes = {...formAttributes};

    if (Array.isArray(updatedAttributes.attributesGoogle)) {
        updatedAttributes.attributesGoogle = updatedAttributes.attributesGoogle.map((item) => {
            if ('group' in item && item.group) {
                return {
                    ...item,
                    items: item.items.map((subItem) => {
                        if (
                            'internalName' in subItem &&
                            typeof subItem.internalName === 'string' &&
                            subItem.internalName
                        ) {
                            const updated = attributesState.get(subItem.internalName);

                            return updated || subItem;
                        }

                        return subItem;
                    }),
                };
            }

            if ('internalName' in item && typeof item.internalName === 'string' && item.internalName) {
                const updated = attributesState.get(item.internalName);

                return updated || item;
            }

            return item;
        }) as typeof updatedAttributes.attributesGoogle;
    }

    if (Array.isArray(updatedAttributes.attributesYandex)) {
        updatedAttributes.attributesYandex = updatedAttributes.attributesYandex.map((item) => {
            if ('internalName' in item && typeof item.internalName === 'string' && item.internalName) {
                const updated = attributesState.get(item.internalName);

                return updated || item;
            }

            return item;
        }) as typeof updatedAttributes.attributesYandex;
    }

    if (Array.isArray(updatedAttributes.attributesDoubleGis)) {
        updatedAttributes.attributesDoubleGis = updatedAttributes.attributesDoubleGis.map((item) => {
            if ('internalName' in item && typeof item.internalName === 'string' && item.internalName) {
                const updatedAttribute = attributesState.get(item.internalName);

                if (
                    updatedAttribute &&
                    (updatedAttribute.featureType === AttributeTypeEnum.GoogleBoolean ||
                        updatedAttribute.featureType === AttributeTypeEnum.YandexBoolean ||
                        updatedAttribute.featureType === AttributeTypeEnum.DoubleGisBoolean)
                ) {
                    return updatedAttribute as BooleanAttributeType;
                }
            }

            return item;
        }) as typeof updatedAttributes.attributesDoubleGis;
    }

    return updatedAttributes;
}
